// ABOUTME: Route host for /crossing/:crossingId — detects auth mode, loads the crossing, and renders the
// ABOUTME: runner frame, which resumes at the crossing's own screen (Issue #1939, #1940).

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import api from '../../services/api';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import CrossingRunnerFrame from './runner/CrossingRunnerFrame';
import type { CrossingDto } from '../../types/api';

export default function CrossingPage() {
  const { crossingId } = useParams<{ crossingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Feedback (2026-09-22): Save & Exit always returned to the dashboard's "All Kids" tab, even
  // when the parent opened Crossing from a specific child's tab. The dashboard tabs are pure
  // component state, not part of the URL, so there's nothing to naturally return to -- carry the
  // originating child id through location.state and hand it back the same way on exit.
  const returnToChildId = (location.state as { returnToChildId?: number } | null)?.returnToChildId ?? null;
  const { data: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  const [crossing, setCrossing] = useState<CrossingDto | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const slug = searchParams.get('slug');
  const token = searchParams.get('token') ?? undefined;
  const isChildMode = !!slug;
  const crossingEnabled = siteSettings?.crossingEnabled ?? false;
  // Flag-off in parent mode is a pure redirect (side effect, no state) -- derived directly below
  // for child mode rather than set via setState in the effect body (react-hooks/set-state-in-effect).
  const flagOffUnavailable = !siteSettingsLoading && !crossingEnabled && isChildMode;

  useEffect(() => {
    if (siteSettingsLoading || !crossingEnabled) return;

    let cancelled = false;

    const load = async () => {
      try {
        const url = isChildMode
          ? `/api/child/${slug}/crossings/${crossingId}`
          : `/api/crossings/${crossingId}`;
        const response = isChildMode && token
          ? await api.get<CrossingDto>(url, { params: { token } })
          : await api.get<CrossingDto>(url);
        if (cancelled) return;
        setCrossing(response.data);
        document.title = 'Crossing · BePono';
      } catch {
        if (cancelled) return;
        if (isChildMode) {
          setLoadFailed(true);
        } else {
          navigate('/', { replace: true });
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [crossingId, slug, token, isChildMode, navigate, crossingEnabled, siteSettingsLoading]);

  useEffect(() => {
    if (!siteSettingsLoading && !crossingEnabled && !isChildMode) {
      navigate('/', { replace: true });
    }
  }, [siteSettingsLoading, crossingEnabled, isChildMode, navigate]);

  if (siteSettingsLoading) {
    return null;
  }

  if (loadFailed || flagOffUnavailable) {
    const returnHref = token ? `/child-dashboard/${slug}?token=${encodeURIComponent(token)}` : `/child-dashboard/${slug}`;
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">This crossing isn&apos;t available</Typography>
        <Link to={returnHref}>Back to your dashboard</Link>
      </Box>
    );
  }

  if (!crossing) {
    return null;
  }

  return (
    <CrossingRunnerFrame
      key={crossing.id}
      initialCrossing={crossing}
      slug={isChildMode ? slug! : undefined}
      token={token}
      returnToChildId={returnToChildId}
    />
  );
}
